import React, { useState } from 'react';
import { Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import {
  FaBox,
  FaIndustry,
  FaShoppingCart,
  FaTools,
  FaFlask,
  FaBoxOpen,
  FaFileContract,
  FaHistory,
  FaBars,
  FaTimes,
  FaSun,
  FaMoon,
  FaSignOutAlt,
  FaPlay,
  FaPause,
} from 'react-icons/fa';
import ProductList from './ProductList';
import ProductContracts from './ProductContracts';
import ContractStages from './ContractStages';
import ContractDetail from './ContractDetail';
import ContractsList from './ContractsList';
import HistoryPage from './HistoryPage';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../auth/AuthContext';
import { ROLE_LABELS, STAGE_GROUPS } from '../auth/roles';
import FallingBackground from './FallingBackground';
import RemnantsPage from './RemnantsPage';
import { FaArchive } from 'react-icons/fa';
import DetailsByStage from './DetailsByStage';

function AppShell() {
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { theme, toggleTheme, bgAnimation, toggleBgAnimation } = useTheme();
  const { user, logout } = useAuth();

  const closeMenu = () => setMenuOpen(false);

  const mainItems = [
    { name: 'Изделия', path: '/', icon: FaBox },
    { name: 'Договоры', path: '/contracts', icon: FaFileContract },
    { name: 'Архив остатков', path: '/remnants', icon: FaArchive },
  ];
  const historyItem = { name: 'История', path: '/history', icon: FaHistory };

  // Все стадии из STAGE_GROUPS
  const stageItems = Object.keys(STAGE_GROUPS).map((key) => ({
    name: STAGE_GROUPS[key].label,
    path: `/details/stage/${key}`,
    icon: STAGE_GROUPS[key].icon || FaBox,
    group: key,
  }));

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  const roleLabel = ROLE_LABELS[user?.role] || user?.role;

  return (
    <div className="app-layout">
      {bgAnimation && <FallingBackground />}
      {menuOpen && <div className="app-overlay" onClick={closeMenu} aria-hidden="true" />}

      <aside className={`app-sidebar ${menuOpen ? 'open' : ''}`}>
        <div className="app-sidebar-header">
          <div className="app-sidebar-brand">
            <FaIndustry style={{ color: 'var(--brand)' }} />
            Производство
          </div>
          <button type="button" className="theme-toggle-btn d-lg-none" onClick={closeMenu} aria-label="Закрыть меню">
            <FaTimes />
          </button>
        </div>

        <nav className="app-sidebar-nav">
          <div className="app-sidebar-section">Обзор</div>
          {mainItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`app-nav-item ${isActive(item.path) ? 'active' : ''}`}
              onClick={closeMenu}
            >
              <item.icon />
              {item.name}
            </Link>
          ))}
          {(user?.role === 'admin' || user?.role === 'manager') && (
            <Link
              to={historyItem.path}
              className={`app-nav-item ${isActive(historyItem.path) ? 'active' : ''}`}
              onClick={closeMenu}
            >
              <historyItem.icon />
              {historyItem.name}
            </Link>
          )}
          <div className="app-sidebar-section">Стадии производства</div>
          {stageItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`app-nav-item ${isActive(item.path) ? 'active' : ''}`}
              onClick={closeMenu}
            >
              <item.icon />
              {item.name}
            </Link>
          ))}
        </nav>

        <div className="app-sidebar-footer">
          {user?.username && (
            <div>
              <strong>{user.username}</strong>
              {roleLabel && <div>{roleLabel}</div>}
            </div>
          )}
        </div>
      </aside>

      <div className="app-main">
        <header className="app-header">
          <button
            type="button"
            className="theme-toggle-btn sidebar-toggle-mobile"
            onClick={() => setMenuOpen(true)}
            aria-label="Открыть меню"
          >
            <FaBars />
          </button>
          <span className="app-header-title d-none d-sm-inline">Система управления производством</span>

          <div className="app-header-actions">
            <button
              className="theme-toggle-btn"
              onClick={toggleBgAnimation}
              title={bgAnimation ? 'Выключить анимированный фон' : 'Включить анимированный фон'}
            >
              {bgAnimation ? <FaPlay style={{ color: 'var(--brand)' }} /> : <FaPause />}
            </button>
            <button type="button" className="theme-toggle-btn" onClick={toggleTheme} aria-label="Тема">
              {theme === 'light' ? <FaMoon /> : <FaSun />}
            </button>
            <button type="button" className="theme-toggle-btn" onClick={handleLogout} aria-label="Выход">
              <FaSignOutAlt />
            </button>
          </div>
        </header>

        <main className="app-content">
          <Routes>
            <Route path="/" element={<ProductList />} />
            <Route path="/production" element={<ContractsList filterGroup="production" />} />
            <Route path="/procurement" element={<ContractsList filterGroup="procurement" />} />
            <Route path="/assembly" element={<ContractsList filterGroup="assembly" />} />
            <Route path="/testing" element={<ContractsList filterGroup="testing" />} />
            <Route path="/packaging" element={<ContractsList filterGroup="packaging" />} />
            <Route path="/details/stage/:stageGroup" element={<DetailsByStage />} />
            <Route path="/remnants" element={<RemnantsPage />} />
            <Route path="/contracts" element={<ContractsList />} />
            <Route path="/history" element={<HistoryPage />} />
            <Route path="/product/:productId/contracts" element={<ProductContracts />} />
            <Route path="/contract/:contractId/stages" element={<ContractStages />} />
            <Route path="/contract/:id" element={<ContractDetail />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}

export default AppShell;